import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import { Message, Loading } from 'element-ui';
import store from '@/store';
import { hasPermission, isSuperAdmin } from '@/common/permission';
import type { UserProfile } from '@/types/user';

import ProxyIndex from '@/views/order/proxy/index.vue';
import Home from '@/views/home.vue';

Vue.use(VueRouter);

/** 路由路径与权限码对应（与 home.vue 菜单配置一致），无权限码的路由仅需登录即可访问 */
const ROUTE_PERMISSION_MAP: Record<string, string> = {
  '/order/order': 'menu.order',
  '/agent': 'menu.agent',
  '/order/proxy': 'menu.order.proxy',
  '/order/proxy-source': 'menu.order.proxy',
  '/order/tokenUsage': 'menu.order.tokenUsage',
  '/order/report': 'menu.order.report',
  '/document/fileUploadRecords': 'menu.document.fileUploadRecords',
  '/admin/tenant': 'menu.admin.tenant',
  '/admin/user': 'menu.admin.user',
  '/admin/permission': 'menu.admin.permission',
  '/admin/device': 'menu.admin.device',
  '/admin/version': 'menu.admin.version',
  '/admin/monitor': 'menu.admin.monitor',
  '/admin/accessLog': 'menu.admin.accessLog',
  '/admin/agentModel': 'menu.admin.agentModel',
  '/admin/agentStats': 'menu.admin.agentStats',
  '/admin/dataCleanup': 'menu.admin.dataCleanup',
  '/toolbox/scale': 'menu.toolbox.scale',
  '/toolbox/delimiter': 'menu.toolbox.scale',
  '/spss': 'menu.spss',
  '/omni': 'menu.omni'
};

/** 是否为需要登录的受保护路由（/home 下的子路径） */
function isProtectedPath(path: string): boolean {
  return (
    path.startsWith('/admin/') ||
    path.startsWith('/order/') ||
    path.startsWith('/document/') ||
    path === '/agent' ||
    path.startsWith('/agent/') ||
    path === '/omni' ||
    path.startsWith('/omni/') ||
    path.startsWith('/toolbox/') ||
    path.startsWith('/spss')
  );
}

/** 与 ROUTE_PERMISSION_MAP 一致；子路径可在此收敛到同一权限码 */
function permissionCodeForPath(path: string): string | undefined {
  const exact = ROUTE_PERMISSION_MAP[path];
  if (exact) return exact;
  if (path.startsWith('/toolbox/')) return ROUTE_PERMISSION_MAP['/toolbox/scale'];
  if (path.startsWith('/spss')) return ROUTE_PERMISSION_MAP['/spss'];
  return undefined;
}

/** 当前用户是否有权访问该路径 */
function canAccessRoute(path: string, user?: UserProfile | null): boolean {
  const code = permissionCodeForPath(path);
  if (!code) return true;
  if (!user) return false;
  if (isSuperAdmin(user) && (!user.permissions || user.permissions.length === 0)) return true;
  return hasPermission(user, code);
}

const adminRouters: RouteConfig[] = [
  {
    path: '/admin/user',
    name: 'UserIndex',
    component: () => import('../views/admin/user/index.vue')
  },
  {
    path: '/admin/tenant',
    name: 'TenantIndex',
    component: () => import('../views/admin/tenant/index.vue')
  },
  {
    path: '/admin/device',
    name: 'DeviceIndex',
    component: () => import('../views/admin/device/index.vue')
  },
  {
    path: '/admin/permission',
    name: 'PermissionIndex',
    component: () => import('../views/admin/permission/index.vue')
  },
  {
    path: '/admin/version',
    name: 'VersionIndex',
    component: () => import('../views/admin/version/index.vue')
  },
  {
    path: '/admin/monitor',
    name: 'MonitorIndex',
    component: () => import('../views/admin/monitor/index.vue')
  },
  {
    path: '/admin/accessLog',
    name: 'AccessLogIndex',
    component: () => import('../views/admin/accessLog/index.vue')
  },
  {
    path: '/admin/agentModel',
    name: 'AgentModelIndex',
    component: () => import('../views/admin/agentModel/index.vue')
  },
  {
    path: '/admin/agentStats',
    name: 'AgentStatsIndex',
    component: () => import('../views/admin/agentStats/index.vue')
  },
  {
    path: '/admin/dataCleanup',
    name: 'DataCleanupIndex',
    component: () => import('../views/admin/dataCleanup/index.vue')
  },
  {
    path: '/admin/billingOverview',
    name: 'BillingOverviewIndex',
    component: () => import('../views/billing/overview/index.vue')
  },
  {
    path: '/admin/billingRecharge',
    name: 'BillingRechargeIndex',
    component: () => import('../views/billing/recharge/index.vue')
  },
  {
    path: '/admin/billingLedger',
    name: 'BillingLedgerIndex',
    component: () => import('../views/billing/ledger/index.vue')
  }
];

const orderRoutes: RouteConfig[] = [
  {
    path: '/order/order',
    name: 'OrderIndex',
    component: () => import('../views/order/order/index.vue')
  },
  {
    path: '/order/proxy',
    name: 'ProxyIndex',
    component: ProxyIndex
  },
  {
    path: '/order/proxy-source',
    redirect: '/order/proxy'
  },
  {
    path: '/order/tokenUsage',
    name: 'TokenUsageIndex',
    component: () => import('../views/order/tokenUsage/index.vue')
  },
  {
    path: '/order/report',
    name: 'ReportIndex',
    component: () => import('../views/order/report/index.vue')
  },
  {
    path: '/order/profileQuestion',
    name: 'ProfileQuestionIndex',
    component: () => import('../views/order/profileQuestion/index.vue')
  }
];

const documentRoutes: RouteConfig[] = [
  {
    path: '/document/fileUploadRecords',
    name: 'FileUploadRecordIndex',
    component: () => import('../views/document/fileUploadRecord/index.vue')
  }
];

const agentRoutes: RouteConfig[] = [
  {
    path: '/agent',
    name: 'AgentIndex',
    component: () => import('../views/agent/index.vue')
  }
];

const omniRoutes: RouteConfig[] = [
  {
    path: '/omni',
    name: 'OmniCustomerServiceIndex',
    component: () => import('../views/omni/index.vue')
  }
];

const toolboxRoutes: RouteConfig[] = [
  {
    path: '/toolbox/scale',
    name: 'ToolboxScale',
    component: () => import('../views/toolbox/scale/index.vue')
  },
  {
    path: '/toolbox/delimiter',
    name: 'ToolboxDelimiter',
    component: () => import('../views/toolbox/delimiter/index.vue')
  }
];

const spssRoutes: RouteConfig[] = [
  {
    path: '/spss',
    name: 'SpssAnalysis',
    component: () => import('../views/spss/index.vue')
  }
];

const routes: RouteConfig[] = [
  {
    path: '/home',
    name: 'Home',
    component: Home,
    redirect: '/dashboard',
    children: [
      {
        path: '/dashboard',
        name: 'PortalIndex',
        component: () => import('../views/portal/index.vue')
      },
      ...adminRouters,
      ...orderRoutes,
      ...documentRoutes,
      ...agentRoutes,
      ...omniRoutes,
      ...toolboxRoutes,
      ...spssRoutes
    ]
  },
  {
    path: '/reception',
    name: 'ReceptionHome',
    component: () => import('../views/reception/home.vue'),
    redirect: { path: 'order-index' },
    children: [
      {
        path: '/reception/order',
        name: 'OrderIndex',
        component: () => import('../views/reception/order/index.vue')
      }
    ]
  }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

// 导航守卫：未登录或无权限时禁止跳转；有 token 时会先拉取用户信息再判断
router.beforeEach(async (to, from, next) => {
  if (to.path === '/') {
    return next({ path: '/home' });
  }
  if (to.path === '/home') {
    return next();
  }
  if (!isProtectedPath(to.path)) {
    return next();
  }
  // 有 token 但 store 无用户时先拉取当前用户（解决刷新后误判为未登录）
  const needFetch = !(store.state as any).user && localStorage.getItem('token');
  const loadingInstance = needFetch ? Loading.service({ fullscreen: true, text: '加载中...' }) : null;
  try {
    await store.dispatch('fetchCurrentUser');
  } finally {
    loadingInstance?.close();
  }
  const { isLoggedIn, user } = store.state as any;
  if (!isLoggedIn) {
    Message.warning('请先登录');
    return next({ path: '/home' });
  }
  if (!canAccessRoute(to.path, user)) {
    Message.warning('无权限访问该页面');
    return next({ path: '/home' });
  }
  next();
});

export default router;
